import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { mapPageTestIDs, Theme } from '@constants';
import { useContextNodeHandle, useContextNodeHandles, useSegmentTrackWeatherMap } from '@hooks';

import { OverlayType, OverlayTypeInfo, ZoomPreset } from './MapTypes';

import changeMapFilter from '../../assets/map/change_map_filter.png';
import changeMapView from '../../assets/map/change_map_view.png';
import { TWCTile } from '../../components/focusable/TWCTile/TWCTile';
import { TWCBackground } from '../primitives/TWCBackground/TWCBackground';
import { TWCText } from '../primitives/TWCText/TWCText';

const tileWidth = 182;
const tileHeight = 90;
const viewMapWidth = tileWidth + tileWidth + 12;
const viewMapHeight = tileHeight;

const ChangeMapSectionHeader = ({ image, text }) => (
  <View style={styles.sectionHeading}>
    <Image source={image} style={styles.sectionHeadingImage} />
    <TWCText noTestID importantForAccessibility={'no'} style={styles.sectionHeadingText} variant={'regularText24'}>
      {text}
    </TWCText>
  </View>
);

ChangeMapSectionHeader.propTypes = {
  text: PropTypes.string.isRequired,
};

const ChangeMapFilterSelector = ({ selectedOverlayType, onOverlayTypeSelected }) => {
  const { t } = useTranslation();
  const titlePath = 'map.overlayTitle.';
  const accessibilityPath = 'map.changeMapTiles.overlayType.';

  // Overlay type tile handles
  const [
    radarTileHandle,
    futureRadarTileHandle,
    alertsTileHandle,
    satelliteTileHandle,
    temperatureTileHandle,
    feelsLikeTileHandle,
    windTileHandle,
    breathingIndexTileHandle,
  ] = useContextNodeHandles([
    mapPageTestIDs.changeMapOverlayTypeRadarButton,
    mapPageTestIDs.changeMapOverlayTypeFutureRadarButton,
    mapPageTestIDs.changeMapOverlayTypeAlertsButton,
    mapPageTestIDs.changeMapOverlayTypeSatelliteButton,
    mapPageTestIDs.changeMapOverlayTypeTemperatureButton,
    mapPageTestIDs.changeMapOverlayTypeFeelsLikeButton,
    mapPageTestIDs.changeMapOverlayTypeWindButton,
    mapPageTestIDs.changeMapOverlayTypeBreathingIndexButton,
  ]);

  // Zoom tile handles
  const [zoomLocalHandle, zoomRegionalHandle, zoomNationalHandle] = useContextNodeHandles([
    mapPageTestIDs.changeMapZoomLocalButton,
    mapPageTestIDs.changeMapZoomRegionalButton,
    mapPageTestIDs.changeMapZoomNationalButton,
  ]);

  // Overlay tile navigation
  const overlayTypeNavigation = {
    [OverlayType.radar]: {
      testID: mapPageTestIDs.changeMapOverlayTypeRadarButton,
      neighbors: {
        up: radarTileHandle,
        left: radarTileHandle,
        down: zoomLocalHandle,
        right: futureRadarTileHandle,
      },
    },
    [OverlayType.futureRadar]: {
      testID: mapPageTestIDs.changeMapOverlayTypeFutureRadarButton,
      neighbors: {
        up: futureRadarTileHandle,
        left: radarTileHandle,
        down: zoomRegionalHandle,
        right: alertsTileHandle,
      },
    },
    [OverlayType.alerts]: {
      testID: mapPageTestIDs.changeMapOverlayTypeAlertsButton,
      neighbors: {
        up: alertsTileHandle,
        left: futureRadarTileHandle,
        down: zoomNationalHandle,
        right: satelliteTileHandle,
      },
    },
    [OverlayType.satellite]: {
      testID: mapPageTestIDs.changeMapOverlayTypeSatelliteButton,
      neighbors: {
        up: satelliteTileHandle,
        left: alertsTileHandle,
        down: zoomNationalHandle,
        right: temperatureTileHandle,
      },
    },
    [OverlayType.temperature]: {
      testID: mapPageTestIDs.changeMapOverlayTypeTemperatureButton,
      neighbors: {
        up: temperatureTileHandle,
        left: satelliteTileHandle,
        down: zoomNationalHandle,
        right: feelsLikeTileHandle,
      },
    },
    [OverlayType.feelsLike]: {
      testID: mapPageTestIDs.changeMapOverlayTypeFeelsLikeButton,
      neighbors: {
        up: feelsLikeTileHandle,
        left: temperatureTileHandle,
        down: zoomNationalHandle,
        right: windTileHandle,
      },
    },
    [OverlayType.wind]: {
      testID: mapPageTestIDs.changeMapOverlayTypeWindButton,
      neighbors: {
        up: windTileHandle,
        left: feelsLikeTileHandle,
        down: zoomNationalHandle,
        right: breathingIndexTileHandle,
      },
    },
    [OverlayType.breathingIndex]: {
      testID: mapPageTestIDs.changeMapOverlayTypeBreathingIndexButton,
      neighbors: {
        up: breathingIndexTileHandle,
        left: windTileHandle,
        down: zoomNationalHandle,
        right: breathingIndexTileHandle,
      },
    },
  };

  const tiles = [];
  const allOverlayTypes = Object.values(OverlayType);
  let index = 1;
  const length = allOverlayTypes.length;

  for (const overlayType of allOverlayTypes) {
    if (!OverlayTypeInfo[overlayType].changeMapSelectable) {
      continue;
    }

    tiles.push(
      <TWCTile
        key={overlayTypeNavigation[overlayType].testID}
        accessibilityHint={`${t(`${accessibilityPath}${overlayType}.accessibilityHint`)} ${
          overlayType === selectedOverlayType ? t(`${accessibilityPath}shared.selectedHint`) : ''
        } ${t(`${accessibilityPath}shared.accessibilityHint`)}`}
        accessibilityLabel={`${t(`${accessibilityPath}${overlayType}.accessibilityLabel`)} ${t(
          `${accessibilityPath}shared.accessibilityLabel`,
          {
            index,
            length,
          },
        )}`}
        gradientType={overlayType === selectedOverlayType ? 'tile' : 'deselectedTile'}
        height={tileHeight}
        nextFocusDown={overlayTypeNavigation[overlayType].neighbors.down}
        nextFocusLeft={overlayTypeNavigation[overlayType].neighbors.left}
        nextFocusRight={overlayTypeNavigation[overlayType].neighbors.right}
        nextFocusUp={overlayTypeNavigation[overlayType].neighbors.up}
        ref={overlayTypeNavigation[overlayType].ref}
        reverseGradient
        testID={overlayTypeNavigation[overlayType].testID}
        width={tileWidth}
        onPress={() => {
          onOverlayTypeSelected(overlayType);
        }}
      >
        <View style={styles.tileContent}>
          <TWCText noTestID variant={'mediumText24'}>
            {t(`${titlePath}${overlayType}`)}
          </TWCText>
        </View>
      </TWCTile>,
    );
    index++;
  }

  return <View style={styles.section}>{tiles}</View>;
};

// ChangeMapFilterSelector.propTypes = {
//   selectedOverlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   onOverlayTypeSelected: PropTypes.func.isRequired,
// };

const ChangeMapViewSelector = ({ selectedZoom, onZoomPresetSelected }) => {
  const { t } = useTranslation();
  const titlePath = 'map.zoomTitle.';
  const accessibilityPath = 'map.changeMapTiles.zoom.';

  // Overlay type tile handles
  const [radarTileHandle, futureRadarTileHandle, alertsTileHandle, satelliteTileHandle] = useContextNodeHandles([
    mapPageTestIDs.changeMapOverlayTypeRadarButton,
    mapPageTestIDs.changeMapOverlayTypeFutureRadarButton,
    mapPageTestIDs.changeMapOverlayTypeAlertsButton,
    mapPageTestIDs.changeMapOverlayTypeSatelliteButton,
  ]);

  // Zoom tile handles
  const [zoomLocalHandle, zoomRegionalHandle, zoomNationalHandle] = useContextNodeHandles([
    mapPageTestIDs.changeMapZoomLocalButton,
    mapPageTestIDs.changeMapZoomRegionalButton,
    mapPageTestIDs.changeMapZoomNationalButton,
  ]);

  // View Map Button Handle
  const viewMapHandle = useContextNodeHandle(mapPageTestIDs.changeMapPageViewMapButton);

  // Zoom tile navigation
  const zoomNavigation = {
    [ZoomPreset.local]: {
      testID: mapPageTestIDs.changeMapZoomLocalButton,
      neighbors: {
        up: radarTileHandle,
        left: zoomLocalHandle,
        down: viewMapHandle,
        right: zoomRegionalHandle,
      },
    },
    [ZoomPreset.regional]: {
      testID: mapPageTestIDs.changeMapZoomRegionalButton,
      neighbors: {
        up: futureRadarTileHandle,
        left: zoomLocalHandle,
        down: viewMapHandle,
        right: zoomNationalHandle,
      },
    },
    [ZoomPreset.national]: {
      testID: mapPageTestIDs.changeMapZoomNationalButton,
      neighbors: {
        up: alertsTileHandle,
        left: zoomRegionalHandle,
        down: viewMapHandle,
        right: satelliteTileHandle,
      },
    },
  };

  const tiles = [];
  let index = 1;
  const allZoomValues = Object.values(ZoomPreset);
  const length = allZoomValues.length;

  for (const zoomValue of Object.values(ZoomPreset)) {
    tiles.push(
      <TWCTile
        key={zoomNavigation[zoomValue].testID}
        accessibilityHint={`${t(`${accessibilityPath}${zoomValue}.accessibilityHint`)} ${
          zoomValue === selectedZoom ? t(`${accessibilityPath}shared.selectedHint`) : ''
        } ${t(`${accessibilityPath}shared.accessibilityHint`)}`}
        accessibilityLabel={`${t(`${accessibilityPath}${zoomValue}.accessibilityLabel`)} ${t(
          `${accessibilityPath}shared.accessibilityLabel`,
          {
            index,
            length,
          },
        )}`}
        gradientType={zoomValue === selectedZoom ? 'tile' : 'deselectedTile'}
        height={tileHeight}
        nextFocusDown={zoomNavigation[zoomValue].neighbors.down}
        nextFocusLeft={zoomNavigation[zoomValue].neighbors.left}
        nextFocusRight={zoomNavigation[zoomValue].neighbors.right}
        nextFocusUp={zoomNavigation[zoomValue].neighbors.up}
        reverseGradient
        testID={zoomNavigation[zoomValue].testID}
        width={tileWidth}
        onPress={() => {
          onZoomPresetSelected(zoomValue);
        }}
      >
        <View style={styles.tileContent}>
          <TWCText noTestID variant={'mediumText24'}>
            {t(`${titlePath}${zoomValue}`)}
          </TWCText>
        </View>
      </TWCTile>,
    );
    index++;
  }

  return <View style={styles.section}>{tiles}</View>;
};

const ViewMapTile = ({ onPress }) => {
  const { t } = useTranslation();
  const tPath = 'map.change.viewMapButton.';

  // Zoom tile handles
  const [zoomLocalHandle, zoomNationalHandle] = useContextNodeHandles([
    mapPageTestIDs.changeMapZoomLocalButton,
    mapPageTestIDs.changeMapZoomNationalButton,
  ]);

  // View Map Button Handle
  const viewMapHandle = useContextNodeHandle(mapPageTestIDs.changeMapPageViewMapButton);

  return (
    <View style={styles.section}>
      <TWCTile
        key={mapPageTestIDs.changeMapPageViewMapButton}
        accessibilityHint={t(`${tPath}accessibilityHint`)}
        accessibilityLabel={t(`${tPath}accessibilityLabel`)}
        gradientType={'deselectedTile'}
        height={viewMapHeight}
        nextFocusDown={viewMapHandle}
        nextFocusLeft={viewMapHandle}
        nextFocusRight={zoomNationalHandle}
        nextFocusUp={zoomLocalHandle}
        reverseGradient
        testID={mapPageTestIDs.changeMapPageViewMapButton}
        width={viewMapWidth}
        onPress={onPress}
      >
        <View style={styles.tileContent}>
          <TWCText noTestID variant={'regularText36'}>
            {t(`${tPath}title`)}
          </TWCText>
        </View>
      </TWCTile>
    </View>
  );
};

// ChangeMapViewSelector.propTypes = {
//   selectedZoom: PropTypes.oneOf(Object.values(ZoomPreset)).isRequired,
//   onZoomPresetSelected: PropTypes.func.isRequired,
// };

// The component shown when the user is selecting a new map to view.
export const ChangeMap = ({ selectedOverlayType, selectedZoom, tileToFocus, setTileToFocus, onNewMapSelected }) => {
  const { t } = useTranslation();
  const tPath = 'map.change.';

  const [highlightedOverlayType, setHighlightedOverlayType] = useState(selectedOverlayType);
  const [highlightedZoom, setHighlightedZoom] = useState(selectedZoom);

  const { setSegmentTrackMapOverlay } = useSegmentTrackWeatherMap();

  return (
    <View style={styles.base}>
      <TWCBackground style={styles.backgroundGradient}>
        <TWCText noTestID importantForAccessibility={'no'} style={styles.topHeading} variant={'regularText42'}>
          {t(`${tPath}topHeading`)}
        </TWCText>
        <ChangeMapSectionHeader image={changeMapFilter} text={t(`${tPath}mapTypeSubheading`)} />
        <ChangeMapFilterSelector
          selectedOverlayType={highlightedOverlayType}
          setTileToFocus={setTileToFocus}
          tileToFocus={tileToFocus}
          onOverlayTypeSelected={newOverlayType => setHighlightedOverlayType(newOverlayType)}
        />
        <ChangeMapSectionHeader image={changeMapView} text={t(`${tPath}mapZoomSubheading`)} />
        <ChangeMapViewSelector
          selectedZoom={highlightedZoom}
          onZoomPresetSelected={newZoomPreset => setHighlightedZoom(newZoomPreset)}
        />
        <ViewMapTile
          onPress={() => {
            setSegmentTrackMapOverlay(highlightedOverlayType, highlightedZoom);
            onNewMapSelected(highlightedOverlayType, highlightedZoom);
          }}
        />
      </TWCBackground>
    </View>
  );
};

// ChangeMap.propTypes = {
//   selectedZoom: PropTypes.oneOf(Object.values(ZoomPreset)).isRequired,
//   selectedOverlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   onNewMapSelected: PropTypes.func.isRequired,
// };

const styles = StyleSheet.create({
  backgroundGradient: {
    flex: 1,
  },
  base: {
    flex: 1,
    overflow: 'hidden',
  },
  section: {
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: Theme.px(35),
    paddingLeft: Theme.px(90),
  },
  sectionHeading: {
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: Theme.px(15),
    paddingLeft: Theme.px(90),
  },
  sectionHeadingImage: {
    height: Theme.px(28),
    resizeMode: 'contain',
    width: Theme.px(28),
  },
  sectionHeadingText: {
    paddingLeft: Theme.px(10),
  },
  tileContent: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  topHeading: {
    paddingBottom: Theme.px(30),
    paddingLeft: Theme.px(90),
    paddingTop: Theme.px(25),
  },
});
